import React, { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useBlocker, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { PDFDocument } from "pdf-lib";
import moment from "moment/moment";
import PrintIcon from "@mui/icons-material/Print";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AutorenewIcon from "@mui/icons-material/Autorenew";

import {
  doneFiled,
  generateReferenceFiled,
  getCorrespondences,
  getFiled,
  getFiledOnSnapshot,
  updateFiled,
} from "../../services/backend/CorrespondenceService";
import { deleteFile } from "../../services/backend/FileSystemService";
import { UploadFile, FILED_LOCKED } from "../../services/backend/UploadFileService";
import { setCorrespondence } from "../../redux/slices/correspondenceSlice";
import { debugLog } from "../../functions";

import LabelQR from "../filed/LabelQR";
import MySnackbar from "../generic/MySnackbar";
import SelectUser from "../generic/SelectUser";
import MyBackdrop from "../generic/MyBackdrop";
import { PERMISSIONS } from "../../constants";
import AnnexesUploader from "../generic/AnnexesUploader";
import BindExpedient from "../expedient/BindExpedient";
import FiledInsertQR from "./FiledInsertQR";
import RadicadoBacklink from "./RadicadoBacklink";
import ForwardResponseDialog from "./ForwardResponseDialog";
import TagMissingDialog from "./TagMissingDialog";
import FiledPreviewDialog from "./FiledPreviewDialog";
import FiledDoneDialog from "./FiledDoneDialog";
import CloseWarningDialog from "./CloseWarningDialog";
import CancelFiledDialog from "./CancelFiledDialog";
import { canCancelFiled } from "./cancelPermission";
import useFiledConfig from "./useFiledConfig";
import { assertPdfNotEncrypted } from "../../services/pdf/validatePdf";

export default function FiledForm() {
  const dispatch = useDispatch();
  const labelQRRef = useRef();
  // Hidden file input shared by the dropzone, "Cambiar archivo" and the doc-first tile
  // (so the OS picker can be opened programmatically within the user gesture).
  const fileInputRef = useRef(null);
  // Imperative handle to FiledInsertQR so the bottom bar can trigger "Aplicar etiqueta".
  const insertQRRef = useRef(null);
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.value);
  const correspondences = useSelector((state) => state.correspondences.value);
  const { reference } = useParams();
  const [searchParams] = useSearchParams();
  const [referenceCode, setReferenceCode] = useState();
  const [referenceId, setReferenceId] = useState();
  const [createdAt, setCreatedAt] = useState();
  const [filedType, setFiledType] = useState("");
  const [folios, setFolios] = useState(0);
  // Flow chosen for a new radicado: 'tag-first' (print label, stick, scan, upload)
  // or 'doc-first' (upload the digital doc first; folios auto-filled from page count).
  const [flowMode, setFlowMode] = useState(null);
  // In doc-first, the file is held client-side until the reference (storage path) exists.
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState(null);
  const [subject, setSubject] = useState();
  const [recipients, setRecipients] = useState([]);
  const [senders, setSenders] = useState([]);
  const [externalFiled, setExternalFiled] = useState();
  const [externalDate, setExternalDate] = useState();
  const [attach, setAttach] = useState();
  const [documentText, setDocumentText] = useState();
  const [fileUrl, setFileUrl] = useState();
  const [fileHash, setFileHash] = useState();
  const [status, setStatus] = useState("OPEN");
  const [loading, setLoading] = useState(false);
  const [infoSnack, setInfoSnack] = useState({ open: false });
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const [annexes, setAnnexes] = useState([]);
  const [expedient, setExpedient] = useState(undefined);
  const [copyInExpedient, setCopyInExpedient] = useState(false);
  const [tagEditing, setTagEditing] = useState(false);
  const [carbonCopy, setCarbonCopy] = useState(undefined);
  const [respondedorUserId, setRespondedorUserId] = useState(null);
  const [respondedorNombre, setRespondedorNombre] = useState("");
  const [parentRadicadoId, setParentRadicadoId] = useState(null);
  const [rootRadicadoId, setRootRadicadoId] = useState(null);
  const [isResponseMode, setIsResponseMode] = useState(false);
  const [openForwardDialog, setOpenForwardDialog] = useState(false);
  const [autoSaveTimeout, setAutoSaveTimeout] = useState(null);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [tagInserted, setTagInserted] = useState(false);
  const [openTagWarning, setOpenTagWarning] = useState(false);
  const [openFiledPreview, setOpenFiledPreview] = useState(false);
  const [openDoneDialog, setOpenDoneDialog] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  // Why the server would refuse to annul this radicado (e.g. a PQRSD's own act).
  const [cancelRefusal, setCancelRefusal] = useState(null);
  // Who may annul also depends on config/filed (the archiver switch).
  const filedConfig = useFiledConfig(user);
  const cancelAccess = canCancelFiled(user, filedConfig);
  const bypassBlockRef = useRef(false);

  // Cache-buster for the PDF iframe: refresh whenever the file changes (new upload /
  // replacement reuses the same storage path) or the tag is re-stamped, otherwise the
  // browser keeps serving the cached, outdated document.
  const version = useMemo(() => new Date().getTime(), [tagEditing, fileUrl, fileHash]);
  const canFinish = useMemo(
    () => subject && recipients.length && senders.length && fileUrl,
    [subject, recipients, senders, fileUrl]
  );

  // Fields still required to be able to finalize (for the inline hint)
  const missingToFinish = useMemo(() => {
    const missing = [];
    if (!subject) missing.push("Asunto");
    if (!recipients.length) missing.push("Destinatario");
    if (!senders.length) missing.push("Remitente");
    if (!fileUrl) missing.push("Archivo PDF");
    return missing;
  }, [subject, recipients, senders, fileUrl]);

  // Summary used by the preview and done modals (FiledViewItem expects a moment createdAt)
  const summaryItem = useMemo(
    () => ({
      filedId: referenceCode,
      referenceId,
      subject,
      filedType,
      folios,
      recipients,
      senders,
      annexes,
      createdAt: createdAt ? moment(createdAt) : moment(),
      fileUrl,
      status: "OPEN",
    }),
    [referenceCode, referenceId, subject, filedType, folios, recipients, senders, annexes, createdAt, fileUrl]
  );

  // Block in-app navigation while an OPEN radicado is being edited
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !bypassBlockRef.current &&
      status === "OPEN" &&
      !!referenceCode &&
      currentLocation.pathname !== nextLocation.pathname
  );

  // Navigate away without triggering the close warning (intentional exits)
  const navigateBypass = (path, opts) => {
    bypassBlockRef.current = true;
    navigate(path, opts);
  };

  // Someone annulled this radicado while the form was open, so the server
  // refused the write. Stop editing and show the annulment; the notice rides
  // along in the navigation state because this form's snackbar unmounts.
  const leaveIfAnnulled = (response) => {
    if (response?.code !== "FILED_CANCELLED") return false;
    if (autoSaveTimeout) clearTimeout(autoSaveTimeout);
    setStatus("CANCELLED");
    navigateBypass(`/filed/cancelled/${referenceCode}`, {
      replace: true,
      state: { notice: "Este radicado fue anulado mientras lo tenías abierto. Tus últimos cambios no se guardaron." },
    });
    return true;
  };

  // Saves this radicado; false when it turned out to be annulled.
  const saveFiled = async (body) => {
    const response = await updateFiled(user.token, body);
    if (leaveIfAnnulled(response)) return false;
    // Finalized elsewhere while this form was open (another tab, another user):
    // lock the form instead of retrying a write the server will keep refusing.
    if (response?.code === FILED_LOCKED) {
      if (autoSaveTimeout) clearTimeout(autoSaveTimeout);
      setStatus("DONE");
      setInfoSnack({ open: true, severity: "warning", message: response.error });
      return false;
    }
    return response;
  };

  const updateCorrespondences = async () => {
    const responseJSON = await getCorrespondences(user.token);
    dispatch(setCorrespondence(responseJSON));
  };

  // Auto-save function
  const autoSave = async () => {
    // Only auto-save if radicado has been generated (has referenceCode) and is not finalized
    if (!referenceCode || status === "DONE" || status === "CANCELLED") {
      return;
    }

    setIsAutoSaving(true);
    try {
      const filed = {
        filedId: referenceCode,
        referenceId,
        subject,
        filedType,
        folios,
        recipients,
        senders,
        carbonCopy,
        externalFiled,
        externalDate,
        attach,
        fileUrl,
        fileHash,
        createdAt,
        annexes,
        expedient: expedient ? { ...expedient, copyInExpedient } : null,
        status, // Preserve status
        tagInserted, // Preserve QR tag flag
        ...(respondedorUserId || parentRadicadoId || rootRadicadoId ? {
          response: {
            ...(respondedorUserId ? {
              respondedorUserId,
              respondedorNombre: respondedorNombre || null
            } : {}),
            ...(parentRadicadoId ? { parentRadicadoId } : {}),
            ...(rootRadicadoId ? { rootRadicadoId } : {})
          }
        } : {})
      };

      if ((await saveFiled(filed)) === false) return false;
      // Don't show snackbar for auto-save to avoid annoying the user
    } catch (error) {
      console.error("Error en auto-guardado:", error);
    } finally {
      setIsAutoSaving(false);
    }
  };

  // Debounced auto-save effect
  useEffect(() => {
    // Don't auto-save on initial load or if radicado doesn't exist yet
    if (!referenceCode || status === "DONE" || status === "CANCELLED") {
      return;
    }

    // Clear existing timeout
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }

    // Set new timeout for auto-save (2 seconds after last change)
    const timeout = setTimeout(() => {
      autoSave();
    }, 2000);

    setAutoSaveTimeout(timeout);

    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [
    referenceCode,
    subject,
    recipients,
    senders,
    carbonCopy,
    externalFiled,
    externalDate,
    attach,
    annexes,
    expedient,
    copyInExpedient,
    respondedorUserId,
    respondedorNombre,
    parentRadicadoId,
    rootRadicadoId,
    status,
    tagInserted,
    fileUrl,
    fileHash
  ]); // eslint-disable-line

  // Best-effort save + native confirm when the browser tab is closed/refreshed
  useEffect(() => {
    const handler = (evt) => {
      if (status !== "OPEN" || !referenceCode || bypassBlockRef.current) return;
      autoSave();
      evt.preventDefault();
      evt.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [status, referenceCode, subject, recipients, senders, fileUrl, tagInserted]); // eslint-disable-line

  // Common payload built from the current form state
  const buildFiled = () => ({
    filedId: referenceCode,
    referenceId,
    subject,
    filedType,
    folios,
    recipients,
    senders,
    carbonCopy,
    externalFiled,
    externalDate,
    attach,
    fileUrl,
    fileHash,
    createdAt,
    annexes,
    tagInserted,
    expedient: expedient ? { ...expedient, copyInExpedient } : null,
    ...(respondedorUserId || parentRadicadoId || rootRadicadoId ? {
      response: {
        ...(respondedorUserId ? {
          respondedorUserId,
          respondedorNombre: respondedorNombre || null
        } : {}),
        ...(parentRadicadoId ? { parentRadicadoId } : {}),
        ...(rootRadicadoId ? { rootRadicadoId } : {})
      }
    } : {})
  });

  // "Guardar" button — persist without finalizing
  const handleSubmit = async (evt) => {
    evt.preventDefault();
    setLoading(true);

    const filed = buildFiled();

    // On save, create ASSIGNED timeline event if responder is assigned for the first time
    if (respondedorUserId && !parentRadicadoId && !rootRadicadoId) {
      const now = new Date();
      filed.timelineEvent = {
        eventType: "ASSIGNED",
        at: now,
        byUserId: user.userUID || user.userId,
        byName: `${user.name || ''} ${user.lastName || ''}`.trim(),
        toUserId: respondedorUserId,
        toName: respondedorNombre || '',
        eventId: `ASSIGNED:none:${respondedorUserId}:${now.getTime()}`
      };
      // Set root to itself for original radicado
      if (!filed.response) filed.response = {};
      filed.response.rootRadicadoId = referenceCode;
    }

    const saved = await saveFiled(filed);
    setLoading(false);
    if (saved === false) return;
    setInfoSnack(
      saved?.result === true
        ? { open: true, message: "Radicado guardado con éxito" }
        : { open: true, severity: "error", message: "No se pudo guardar el radicado. Inténtalo de nuevo." }
    );
  };

  // Finalize the radicado (called after the preview confirmation)
  const finalizeFiled = async () => {
    const filed = buildFiled();

    if (isResponseMode && parentRadicadoId && rootRadicadoId) {
      // This is a response being finalized
      const now = new Date();
      filed.timelineEvent = {
        eventType: "RESPONSE_FILED",
        at: now,
        byUserId: user.userUID || user.userId,
        byName: `${user.name || ''} ${user.lastName || ''}`.trim(),
        documentId: referenceCode,
        documentName: `${referenceCode}.pdf`,
        eventId: `RESPONSE_FILED:${referenceCode}:none:${now.getTime()}`
      };
      filed.timelineRootRadicadoId = rootRadicadoId;
    } else if (respondedorUserId && !parentRadicadoId) {
      // Original radicado with responder assigned
      const now = new Date();
      filed.timelineEvent = {
        eventType: "ASSIGNED",
        at: now,
        byUserId: user.userUID || user.userId,
        byName: `${user.name || ''} ${user.lastName || ''}`.trim(),
        toUserId: respondedorUserId,
        toName: respondedorNombre || '',
        eventId: `ASSIGNED:none:${respondedorUserId}:${now.getTime()}`
      };
      if (!filed.response) filed.response = {};
      filed.response.rootRadicadoId = referenceCode;
    } else if (!parentRadicadoId) {
      // Original radicado being finalized - add FILED event
      const now = new Date();
      filed.timelineEvent = {
        eventType: "FILED",
        at: now,
        byUserId: user.userUID || user.userId,
        byName: `${user.name || ''} ${user.lastName || ''}`.trim(),
        documentId: referenceCode,
        documentName: `${referenceCode}.pdf`,
        eventId: `FILED:${referenceCode}:none:${now.getTime()}`
      };
      if (!filed.response) filed.response = {};
      filed.response.rootRadicadoId = referenceCode;
    }

    const response = await doneFiled(user.token, filed);
    if (leaveIfAnnulled(response)) return false;
    // Already finalized (a double click, or another tab got there first): the
    // server refused to finalize it twice, which is the outcome we wanted.
    if (response?.code === FILED_LOCKED) return true;
    if (response?.result !== true) {
      throw new Error(response?.error || "No se pudo radicar el documento");
    }
    return true;
  };

  // "Finalizar" button — open the tag warning (if needed) or the preview modal
  const handleFinalizeClick = () => {
    if (!canFinish) return;
    if (!tagInserted) {
      setOpenTagWarning(true);
    } else {
      setOpenFiledPreview(true);
    }
  };

  // Preview "Confirmar" — autosave then finalize, then show the done modal
  const handleConfirmFinalize = async () => {
    setFinalizing(true);
    try {
      if ((await autoSave()) === false) return;
      if (!(await finalizeFiled())) return;
      // Radicado is now finalized: stop the blocker/auto-save and lock the form
      setStatus("DONE");
      setOpenFiledPreview(false);
      setOpenDoneDialog(true);
    } catch (error) {
      console.error("Error al radicar:", error);
      setInfoSnack({ open: true, severity: "error", message: error?.message || "No se pudo radicar el documento" });
    } finally {
      setFinalizing(false);
    }
  };

  const handleFiledGenerate = async () => {
    setLoading(true);
    const responseJSON = await generateReferenceFiled(user.token, {
      correspondenceId: filedType,
      filedType,
      folios,
    });

    if (!responseJSON || !responseJSON.referenceCode) {
      setInfoSnack({
        open: true,
        message: "No se pudo generar el radicado. Inténtalo de nuevo.",
      });
      setLoading(false);
      return;
    }

    setReferenceCode(responseJSON.referenceCode);
    setReferenceId(responseJSON.referenceId);
    setCreatedAt(responseJSON.createdAt);
    
    // If this is response mode, create RESPONSE_CREATED timeline event
    if (isResponseMode && parentRadicadoId && rootRadicadoId) {
      const now = new Date();
      const timelineEvent = {
        eventType: "RESPONSE_CREATED",
        at: now,
        byUserId: user.userUID || user.userId,
        byName: `${user.name || ''} ${user.lastName || ''}`.trim(),
        documentId: responseJSON.referenceCode,
        documentName: `${responseJSON.referenceCode}.pdf`,
        eventId: `RESPONSE_CREATED:${responseJSON.referenceCode}:none:${now.getTime()}`
      };
      // Update parent with timeline event (will be handled by backend)
      await updateFiled(user.token, {
        filedId: parentRadicadoId,
        timelineEvent: timelineEvent,
        timelineRootRadicadoId: rootRadicadoId
      });
    }
    
    // doc-first: now that the storage path (referenceCode) exists, upload the held file.
    // Use responseJSON.referenceCode — the referenceCode state isn't updated in this
    // closure yet (same caveat noted in handleFileUpload).
    if (pendingFile) {
      const code = responseJSON.referenceCode;
      const bucket = process.env.REACT_APP_STORAGE_BUCKET_FILED;
      const uploaded = await UploadFile(
        user,
        pendingFile,
        bucket,
        `${code}/${code}.pdf`,
        false,
        { filedId: code },
        { returnRefusal: true }
      );
      if (uploaded && uploaded.downloadUrl) {
        setFileUrl(uploaded.downloadUrl);
        setFileHash(uploaded.fileHash);
        setTagInserted(false);
        await updateFiled(user.token, {
          ...buildFiled(),
          filedId: code,
          fileUrl: uploaded.downloadUrl,
          fileHash: uploaded.fileHash,
          tagInserted: false,
        });
      } else {
        setInfoSnack({ open: true, message: uploaded?.error || "No se pudo subir el documento" });
      }
      handleClearPending();
    }

    setLoading(false);

    if (!reference) {
      // This is our own URL swap to the freshly created radicado — bypass the
      // unsaved-changes guard so it doesn't pop the exit warning, then re-enable it
      // so leaving the page later still prompts.
      bypassBlockRef.current = true;
      navigate(`/filed/${responseJSON.referenceCode}`, { replace: true });
      setTimeout(() => {
        bypassBlockRef.current = false;
      }, 0);
    }
  };

  const handleDeleteUpload = async () => {
    if (refuseIfLocked()) return;
    setOpenBackdrop(true);
    const localFile = referenceCode + "/" + referenceCode + ".pdf";
    debugLog("localFile: ", localFile);
    const bucket = process.env.REACT_APP_STORAGE_BUCKET_FILED;
    try {
      const response = await deleteFile(user.token, bucket, localFile);
      debugLog("response: ", response);
      // The server refused (radicado finalized or annulled meanwhile, or its
      // status unreadable): the file is still there, so keep showing it.
      if (response && response.result === false && response.code) {
        setInfoSnack({ open: true, severity: "error", message: response.error || "No se pudo quitar el archivo" });
        if (response.code === FILED_LOCKED) setStatus("DONE");
        return;
      }
      setFileUrl(null);
    } catch (error) {
      debugLog("response: ", error);
      setFileUrl(null);
    } finally {
      setOpenBackdrop(false);
    }
  };

  // A finalized or annulled radicado's document can't change. The server
  // enforces it; this spares the request and says why.
  const refuseIfLocked = () => {
    if (status !== "DONE" && status !== "CANCELLED") return false;
    setInfoSnack({
      open: true,
      severity: "error",
      message: status === "CANCELLED"
        ? "El radicado fue anulado; su contenido no se puede modificar."
        : "El radicado ya fue formalizado; su contenido no se puede modificar.",
    });
    return true;
  };

  // Revoke the local preview URL when it changes or on unmount
  useEffect(() => () => {
    if (pendingPreviewUrl) URL.revokeObjectURL(pendingPreviewUrl);
  }, [pendingPreviewUrl]);

  const handleClearPending = () => {
    if (pendingPreviewUrl) URL.revokeObjectURL(pendingPreviewUrl);
    setPendingFile(null);
    setPendingPreviewUrl(null);
  };

  // doc-first: hold the selected PDF locally and auto-fill folios from its page count.
  // The actual upload happens in handleFiledGenerate once the reference (path) exists.
  const handleSelectFile = async (file) => {
    if (!file) return;
    setLoading(true);
    try {
      const message = await assertPdfNotEncrypted(file);
      if (message) {
        setInfoSnack({ open: true, message });
        return;
      }
      const doc = await PDFDocument.load(await file.arrayBuffer(), {
        ignoreEncryption: true,
      });
      setFolios(doc.getPageCount());
      if (pendingPreviewUrl) URL.revokeObjectURL(pendingPreviewUrl);
      setPendingFile(file);
      setPendingPreviewUrl(URL.createObjectURL(file));
    } catch (error) {
      debugLog("handleSelectFile error: ", error);
      setInfoSnack({ open: true, message: "No se pudo leer el documento PDF" });
    } finally {
      setLoading(false);
    }
  };

  // Resolves true when the file was stored, false otherwise (FiledInsertQR
  // only marks the tag as applied on true).
  const handleFileUpload = async (event, isNewFile = true) => {
    // Checked before anything is sent: on 2026-11-03 this upload went out for a
    // radicado already finalized and replaced its filed PDF.
    if (refuseIfLocked()) return false;
    let uploadedOk = false;
    setLoading(true);
    let localFile = event.target.files[0];
    if (localFile) {
      const correspondenceId = referenceCode;
      const bucket = process.env.REACT_APP_STORAGE_BUCKET_FILED;

      const response = await UploadFile(
        user,
        localFile,
        bucket,
        `${correspondenceId}/${correspondenceId}.pdf`,
        false,
        isNewFile ? { filedId: correspondenceId } : undefined,
        { returnRefusal: true }
      );

      if (response && response.result === false && response.code) {
        setInfoSnack({ open: true, severity: "error", message: response.error });
        if (response.code === FILED_LOCKED) setStatus("DONE");
      } else if (response && response.downloadUrl) {
        uploadedOk = true;
        setFileUrl(response.downloadUrl);
        setFileHash(response.fileHash);
        // A brand-new PDF has no QR tag stamped yet
        const nextTagInserted = isNewFile ? false : tagInserted;
        if (isNewFile) setTagInserted(false);
        // Persist immediately so the uploaded file isn't lost (debounced auto-save
        // does not watch fileUrl, and state isn't updated yet in this closure)
        if (referenceCode && status !== "DONE" && status !== "CANCELLED") {
          await saveFiled({
            ...buildFiled(),
            fileUrl: response.downloadUrl,
            fileHash: response.fileHash,
            tagInserted: nextTagInserted,
          });
        }
      } else {
        console.error("Error al subir el archivo:", response);
        setInfoSnack({ open: true, severity: "error", message: "No se pudo subir el documento" });
      }
    }
    setLoading(false);
    return uploadedOk;
  };

  // Annulled server-side by CancelFiledDialog; from here the form is read-only.
  const handleFiledCancelled = () => {
    if (autoSaveTimeout) clearTimeout(autoSaveTimeout);
    setOpenCancelDialog(false);
    setStatus("CANCELLED");
    navigateBypass(`/filed/cancelled/${referenceCode}`, { replace: true });
  };

  const onPrintLabel = () => {
    // Crear un elemento de estilo y añadir estilos para la impresión.
    const printStyle = document.createElement("style");
    printStyle.type = "text/css";
    printStyle.innerHTML = `
    @media print {
      @page {
        size: 20cm 4cm; /* Tamaño de la etiqueta a imprimir */
        margin: 0; /* Eliminar márgenes de la página */
      }
      body, html {
        margin: 0;
        padding: 0;
        overflow: hidden; /* Evitar el desbordamiento de la página */
        width: 20cm;
        height: 4cm;
      }
      body * {
        visibility: hidden; /* Ocultar todo el contenido */
        max-height: none !important;
      }
      .printable, .printable * {
        visibility: visible; /* Solo mostrar el contenido del elemento a imprimir */
        max-width: 20cm; /* Ajustar el ancho del contenido a imprimir */
        max-height: 4cm; /* Ajustar la altura del contenido a imprimir */
        overflow: hidden; /* Asegurarse de que no haya desbordamiento */
      }
      .printable {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
      }
    }
  `;
    // Añadir el elemento de estilo al documento.
    document.head.appendChild(printStyle);

    // Asegurarse de que el elemento a imprimir tenga la clase 'printable'.
    labelQRRef.current.classList.add("printable");

    window.onafterprint = function () {
      // Eliminar los estilos añadidos después de imprimir.
      document.head.removeChild(printStyle);
      // Restaurar la visibilidad y eliminar la clase 'printable'.
      labelQRRef.current.classList.remove("printable");
      window.onafterprint = null;
    };

    // Llamar a la función de impresión del navegador.
    window.print();
  };

  useEffect(() => {
    if ((!reference && !user.permissions.includes(PERMISSIONS.FILED.CREATE))
      || (reference && !user.permissions.includes(PERMISSIONS.FILED.UPDATE))) {
      return navigate("/filedlist");
    }
    
    // Check if this is response mode
    const parentId = searchParams.get("parentRadicadoId");
    const responseMode = searchParams.get("responseMode") === "true";
    
    if (responseMode && parentId && !reference && correspondences) {
      setIsResponseMode(true);
      // Responses keep the original (tag-first) flow — skip the mode menu.
      setFlowMode("tag-first");
      // Load parent radicado data
      (async () => {
        const parentData = await getFiled(user.token, parentId);
        if (parentData) {
          setParentRadicadoId(parentData.filedId);
          const rootId = parentData.response?.rootRadicadoId || parentData.filedId;
          setRootRadicadoId(rootId);
          // Find "Response" tipo from correspondences (name is "response" in English)
          // Try multiple ways to find it since structure might vary
          let respuestaTipo = correspondences.find(
            (cor) => {
              const subserieName = (cor.subserieName || "").toLowerCase().trim();
              return (subserieName === "response" || subserieName === "respuesta") && cor.resultType === 'filed';
            }
          );
          
          // If not found, try alternative matching
          if (!respuestaTipo) {
            respuestaTipo = correspondences.find(
              (cor) => {
                const correspondenceId = (cor.correspondenceId || "").toLowerCase();
                return correspondenceId === "response" || 
                       correspondenceId === "respuesta" || 
                       correspondenceId.includes("response") ||
                       correspondenceId.includes("respuesta");
              }
            );
          }
          
          if (respuestaTipo) {
            // Use correspondenceId if available
            if (respuestaTipo.correspondenceId) {
              setFiledType(respuestaTipo.correspondenceId);
            } else {
              // If no correspondenceId, try to find it by matching subserieName again
              const match = correspondences.find(
                c => (c.subserieName?.toLowerCase() === "response" || c.subserieName?.toLowerCase() === "respuesta") && c.resultType === 'filed'
              );
              if (match?.correspondenceId) {
                setFiledType(match.correspondenceId);
              } else {
                console.error("No se pudo encontrar el correspondenceId para Response. Correspondencias disponibles:", correspondences.map(c => ({ subserieName: c.subserieName, correspondenceId: c.correspondenceId })));
              }
            }
          } else {
            console.error("No se encontró el tipo 'Response' en las correspondencias. Buscando en:", correspondences.map(c => c.subserieName));
          }
          // Auto-attach original PDF as first annex
          if (parentData.fileUrl) {
            const originalAnnex = {
              id: `original-${parentData.filedId}`,
              name: `${parentData.filedId}.pdf`,
              url: parentData.fileUrl,
              fileHash: parentData.fileHash
            };
            setAnnexes([originalAnnex]);
          }
          // Set responder from parent if not already set
          if (parentData.response?.respondedorUserId && !respondedorUserId) {
            setRespondedorUserId(parentData.response.respondedorUserId);
            setRespondedorNombre(parentData.response.respondedorNombre || "");
          }
          // Prefill recipients and senders from parent radicado
          // In a response: recipients should be the original senders, senders should be the original recipients
          if (parentData.recipients && parentData.recipients.length > 0) {
            setSenders(parentData.recipients);
          }
          if (parentData.senders && parentData.senders.length > 0) {
            setRecipients(parentData.senders);
          }
        }
      })();
    }
    
    if (reference && !referenceId) {
      (async () => {
        let responseJSON = await getFiled(user.token, reference);
        if (!responseJSON || responseJSON.result === false) return navigate("/filedlist");
        if (responseJSON.status === "CANCELLED") {
          bypassBlockRef.current = true;
          return navigate(`/filed/cancelled/${responseJSON.filedId}`, { replace: true });
        }
        setReferenceCode(responseJSON.filedId);
        setReferenceId(responseJSON.referenceId);
        setCreatedAt(responseJSON.createdAt);
        setFiledType(responseJSON.filedType);
        setFolios(responseJSON.folios);
        setSubject(responseJSON.subject);
        setRecipients(responseJSON.recipients || []);
        setCarbonCopy(responseJSON.carbonCopy);
        setSenders(responseJSON.senders || []);
        setExternalFiled(responseJSON.externalFiled);
        setExternalDate(responseJSON.externalDate);
        setAttach(responseJSON.attach);
        setFileUrl(responseJSON.fileUrl);
        setFileHash(responseJSON.fileHash);
        setDocumentText(responseJSON.documentText);
        setCancelRefusal(responseJSON.cancelRefusal || null);
        setStatus(responseJSON.status);
        setAnnexes(responseJSON.annexes ? responseJSON.annexes : []);
        setTagInserted(responseJSON.tagInserted || false);
        setExpedient(responseJSON.expedient || undefined);
        setCopyInExpedient(responseJSON.expedient?.copyInExpedient);
        setRespondedorUserId(responseJSON.response?.respondedorUserId || null);
        setRespondedorNombre(responseJSON.response?.respondedorNombre || null);
        setParentRadicadoId(responseJSON.response?.parentRadicadoId || null);
        setRootRadicadoId(responseJSON.response?.rootRadicadoId || null);
        // != null on purpose: a radicado without `response` (undefined) is not a response.
        setIsResponseMode(responseJSON.filedType === "respuesta" || responseJSON.response?.parentRadicadoId != null);
      })();
    }
    if (!correspondences) {
      updateCorrespondences();
    }
  }, [searchParams, correspondences]); // eslint-disable-line

  useEffect(() => {
    if (referenceCode && !user.permissions.includes(PERMISSIONS.FILED.UPDATE)) {
      return navigate("/filedlist");
    }

    if (referenceCode && !documentText) {
      const unsubscribe = getFiledOnSnapshot("filed", referenceCode, (doc) => {
        if (doc.documentText) setDocumentText(doc.documentText);
      });

      return () => unsubscribe();
    }
  }, [referenceCode]); // eslint-disable-line

  return (
    <>
      <Container maxWidth={false}>
        <Grid container sx={{ mb: 1 }}>
          <Grid item xs={6}>
              <Box component="form" noValidate onSubmit={handleSubmit} sx={{ m: 1 }}>
              {isAutoSaving && referenceCode && status !== "DONE" && (
                <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    Guardando automáticamente...
                  </Typography>
                </Box>
              )}
              {/* Step 1 (new radicado): choose the workflow before configuring anything */}
              {!referenceCode && !flowMode && (
                <Box sx={{ py: 2 }}>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 2, textAlign: "center" }}
                  >
                    ¿Cómo quieres radicar?
                  </Typography>
                  <Grid container spacing={2} justifyContent="center">
                    {[
                      {
                        key: "tag-first",
                        label: "Hacer etiqueta primero",
                        description:
                          "Aún no tienes el documento digital. Genera e imprime la etiqueta, pégala físicamente en el documento, escanéalo y cárgalo. ",
                        highlight: "Ideal para documentos físicos.",
                        icon: <LocalOfferIcon sx={{ fontSize: { xs: 40, md: 52 } }} />,
                      },
                      {
                        key: "doc-first",
                        label: "Cargar documento",
                        description:
                          "Ya tienes el documento. Cárgalo primero; los folios se completan solos y podrás colocar la etiqueta sobre el PDF. ",
                        highlight: "Ideal para documentos digitales.",
                        icon: <CloudUploadIcon sx={{ fontSize: { xs: 40, md: 52 } }} />,
                      },
                    ].map((opt) => (
                      <Grid item xs={12} sm={6} key={opt.key}>
                        <Card
                          sx={{
                            height: "100%",
                            transition: "box-shadow 0.2s ease, transform 0.2s ease",
                            "&:hover": { boxShadow: 6, transform: "translateY(-4px)" },
                          }}
                        >
                          <CardActionArea
                            onClick={() => {
                              setFlowMode(opt.key);
                              // doc-first: open the file picker right away (stay inside
                              // the click gesture so the browser allows the dialog).
                              if (opt.key === "doc-first") {
                                fileInputRef.current?.click();
                              }
                            }}
                            sx={{ height: "100%" }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                textAlign: "center",
                                p: 3,
                              }}
                            >
                              <Box sx={{ color: "primary.main", mb: 1.5 }}>{opt.icon}</Box>
                              <Typography variant="h6" gutterBottom>
                                {opt.label}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {opt.description}
                                <Box component="span" sx={{ fontWeight: "bold" }}>
                                  {opt.highlight}
                                </Box>
                              </Typography>
                            </Box>
                          </CardActionArea>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Mode chosen (new radicado): show which mode + allow changing it */}
              {!referenceCode && flowMode && (
                <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {flowMode === "tag-first"
                      ? "Modo: Hacer etiqueta primero"
                      : "Modo: Cargar documento"}
                  </Typography>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => {
                      setFlowMode(null);
                      handleClearPending();
                    }}
                  >
                    Cambiar
                  </Link>
                </Box>
              )}

              {/* Step 2: configure filed type + folios (after a mode is chosen, or editing) */}
              {(flowMode || referenceCode) && (
                <Grid container spacing={1}>
                  <Grid item xs={8}>
                    <FormControl fullWidth sx={{ marginY: 1 }}>
                      <InputLabel id="filed-type-label">
                        Tipo de Radicado
                      </InputLabel>
                      {correspondences
                        && <Select
                          labelId="filed-type-label"
                          label="Tipo de Radicado"
                          required
                          value={filedType}
                          onChange={(evt) => setFiledType(evt.target.value)}
                          disabled={!!referenceCode || isResponseMode}
                        >
                          {correspondences.filter((cor) => cor.resultType === 'filed').map((cor) => (
                            <MenuItem key={cor.correspondenceId} value={cor.correspondenceId}>
                              {cor.subserieName}
                            </MenuItem>
                          ))}
                        </Select>
                      }
                      {isResponseMode && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Tipo: Respuesta (bloqueado)
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl fullWidth sx={{ marginY: 1 }}>
                      <TextField
                        label="No de Folios"
                        required
                        value={folios}
                        type="number"
                        inputProps={{ min: 1 }}
                        onChange={(evt) => {
                          const value = parseInt(evt.target.value, 10);
                          if (!isNaN(value) && value > 0) {
                            setFolios(value);
                          } else if (evt.target.value === "" || evt.target.value === null || isNaN(value)) {
                            setFolios(0);
                          }
                        }}
                        disabled={!!referenceCode}
                        error={folios <= 0}
                        helperText={
                          folios <= 0
                            ? "El número de folios debe ser mayor a 0"
                            : flowMode === "doc-first"
                              ? "Se autocompleta del documento cargado"
                              : ""
                        }
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              )}

              <Box sx={{ display: referenceCode ? "block" : "none" }}>
                {(!reference || referenceId) && (
                  <>
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{ display: "block", mt: 1, mb: 0.5 }}
                    >
                      Datos del documento
                    </Typography>
                    <Divider sx={{ mb: 1.5 }} />
                    <FormControl fullWidth sx={{ mb: 1 }}>
                      <TextField
                        label="Asunto"
                        required
                        rows={3}
                        fullWidth
                        autoFocus
                        multiline
                        defaultValue={subject}
                        disabled={status === "DONE"}
                        error={status !== "DONE" && !subject}
                        helperText={status !== "DONE" && !subject ? "El asunto es obligatorio" : ""}
                        onChange={(evt) => setSubject(evt.target.value)}
                        onBlur={() => autoSave()}
                      />
                    </FormControl>
                    <FormControl fullWidth sx={{ mb: 1, flexDirection: "row", alignItems: "center" }}>
                      <SelectUser
                        multiple
                        required
                        error={status !== "DONE" && !recipients.length}
                        userType={
                          ["internal", "received"].includes(filedType)
                            ? "users"
                            : "people"
                        }
                        noedit={["internal", "received"].includes(filedType)}
                        value={recipients}
                        setValue={setRecipients}
                        disabled={status === "DONE"}
                        placeholder="Destinatario"
                        onChange={(__, value) => setRecipients(value)}
                      />
                      <Box sx={{ borderRadius: 1, ml: 1, backgroundColor: "#efefef", px: 1 }}>
                        C.C.
                        <Checkbox
                          defaultChecked={!!carbonCopy}
                          disabled={status === "DONE"}
                          onChange={(evt) => {
                            setCarbonCopy(evt.target.checked ? [] : undefined);
                          }}
                        />
                      </Box>
                    </FormControl>
                    {carbonCopy
                      && <FormControl fullWidth sx={{ mb: 1 }}>
                        <SelectUser
                          freeSolo
                          multiple
                          noedit={true}
                          value={carbonCopy}
                          setValue={setCarbonCopy}
                          disabled={status === "DONE"}
                          placeholder="Con copia"
                          onChange={(__, value) => {
                            const recipnts = value.filter((val) => {
                              if (typeof val === "string") return /\S+@\S+\.\S+/.test(val);
                              return true;
                            });
                            setCarbonCopy(recipnts);
                          }}
                        />
                      </FormControl>
                    }
                    <FormControl fullWidth sx={{ mb: 1 }}>
                      <SelectUser
                        multiple={true}
                        required
                        error={status !== "DONE" && !senders.length}
                        userType={
                          ["internal", "external"].includes(filedType)
                            ? "users"
                            : "people"
                        }
                        noedit={["internal", "external"].includes(filedType)}
                        value={senders}
                        setValue={setSenders}
                        disabled={status === "DONE"}
                        placeholder="Remitente"
                        onChange={(__, value) => setSenders(value)}
                      />
                    </FormControl>

                    {isResponseMode && parentRadicadoId && (
                      <RadicadoBacklink parentRadicadoId={parentRadicadoId} />
                    )}
                    
                    {isResponseMode && status !== "DONE" && respondedorUserId && (respondedorUserId === user?.userUID || respondedorUserId === user?.userId) && (
                      <Box sx={{ mb: 2 }}>
                        <Button
                          variant="outlined"
                          onClick={() => setOpenForwardDialog(true)}
                        >
                          Reenviar a otro respondedor
                        </Button>
                      </Box>
                    )}

                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{ display: "block", mt: 2, mb: 0.5 }}
                    >
                      Información externa
                    </Typography>
                    <Divider sx={{ mb: 1.5 }} />
                    <Grid container spacing={2} sx={{ mb: 1 }}>
                      <Grid item xs={6}>
                        <FormControl fullWidth>
                          <TextField
                            label="Radicado externo"
                            fullWidth
                            defaultValue={externalFiled}
                            disabled={status === "DONE"}
                            onChange={(evt) =>
                              setExternalFiled(evt.target.value)
                            }
                            onBlur={() => autoSave()}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={6}>
                        <FormControl fullWidth>
                          <DatePicker
                            label="Fecha externa"
                            defaultValue={
                              (externalDate && moment(externalDate)) ||
                              undefined
                            }
                            onChange={(momt) => setExternalDate(momt)}
                            disabled={status === "DONE"}
                          />
                        </FormControl>
                      </Grid>
                    </Grid>

                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{ display: "block", mt: 2, mb: 0.5 }}
                    >
                      Anexos y expediente
                    </Typography>
                    <Divider sx={{ mb: 1.5 }} />
                    <AnnexesUploader
                      referenceCode={referenceCode}
                      annexes={annexes}
                      setAnnexes={setAnnexes}
                      attach={attach}
                      setAttach={setAttach}
                      disabled={status === "DONE" || status === "CANCELLED"}
                    />
                    
                    <BindExpedient
                      expedient={expedient}
                      setExpedient={setExpedient}
                      copyInExpedient={copyInExpedient}
                      setCopyInExpedient={setCopyInExpedient}
                    />

                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{ display: "block", mt: 2, mb: 0.5 }}
                    >
                      Contenido del documento
                    </Typography>
                    <Divider sx={{ mb: 1.5 }} />
                    <FormControl fullWidth sx={{ mb: 1 }}>
                      <TextField
                        label={!documentText && "Contenido del documento"}
                        defaultValue={documentText}
                        rows={3}
                        fullWidth
                        multiline
                        disabled
                      />
                    </FormControl>
                  </>
                )}
              </Box>

              <Box sx={{ textAlign: "center" }}>
                <LabelQR
                  ref={labelQRRef}
                  filed={{
                    referenceId,
                    referenceCode,
                    folios,
                    createdAt,
                    type: filedType
                  }}
                />
                {referenceCode && (
                  <Tooltip title="Imprimir" placement="top">
                    <IconButton onClick={onPrintLabel} color="primary">
                      <PrintIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              <Box
                justifyContent="center"
                sx={{ display: !referenceCode && flowMode ? "flex" : "none", mt: 2 }}
              >
                <Button
                  type="button"
                  disabled={
                    !filedType
                    || !folios
                    || folios <= 0
                    || loading
                    || (flowMode === "doc-first" && !pendingFile)
                  }
                  variant="contained"
                  onClick={handleFiledGenerate}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    "Generar etiqueta"
                  )}
                </Button>
              </Box>

              <Grid
                container
                spacing={2}
                sx={{
                  position: "sticky",
                  bottom: 0,
                  zIndex: 3,
                  mt: 1,
                  py: 1.5,
                  backgroundColor: "background.paper",
                  borderTop: 1,
                  borderColor: "divider",
                }}
                textAlign="center"
                display={referenceCode ? "flex" : "none"}
              >
                {status === "OPEN" && (
                  <>
                    <Grid item xs={4}>
                      <Tooltip
                        title={cancelRefusal || cancelAccess.reason || ""}
                        disableHoverListener={!cancelRefusal && !cancelAccess.reason}
                      >
                        <span>
                          <Button
                            variant="contained"
                            type="button"
                            disabled={loading || !!cancelRefusal || !cancelAccess.allowed}
                            onClick={() => setOpenCancelDialog(true)}
                          >
                            {loading ? <CircularProgress size={24} /> : <span>Anular</span>}
                          </Button>
                        </span>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={4}>
                      <Button
                        variant="contained"
                        type="button"
                        onClick={handleFinalizeClick}
                        disabled={!canFinish || loading}
                      >
                        {canFinish && loading ? (
                          <CircularProgress size={24} />
                        ) : (
                          "Finalizar"
                        )}
                      </Button>
                    </Grid>
                    <Grid item xs={4}>
                      <Button
                        variant="contained"
                        type="submit"
                        disabled={loading}
                      >
                        {loading ? <CircularProgress size={24} /> : <span>Guardar</span>}
                      </Button>
                    </Grid>
                    {!canFinish && (
                      <Grid item xs={12}>
                        <Alert severity="info" sx={{ textAlign: "left" }}>
                          Puedes guardar el borrador en cualquier momento. Para
                          finalizar faltan estos campos obligatorios:{" "}
                          <strong>{missingToFinish.join(", ")}</strong>.
                        </Alert>
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </Box>
          </Grid>

          <Grid item xs={6} sx={{ minHeight: 600, textAlign: "center", position: "relative" }}>
            {/* Single shared picker: opened from the doc-first tile, the dropzone and
                "Cambiar archivo". Routes by current state. */}
            <input
              ref={fileInputRef}
              hidden
              accept="application/pdf"
              type="file"
              onChange={(evt) => {
                const file = evt.target.files[0];
                evt.target.value = null;
                if (!file) return;
                if (referenceCode) {
                  if (refuseIfLocked()) return;
                  handleFileUpload({ target: { files: [file] } });
                } else {
                  handleSelectFile(file);
                }
              }}
            />
            {fileUrl ? (
              tagEditing ? (
                // Tag-placement view stays in the right column, bounded to the viewport
                // as a flex column so the action bar at the bottom is always reserved
                // (and visible) below the scrollable rendered pages.
                <Box
                  sx={(theme) => ({
                    position: "sticky",
                    top: theme.spacing(2),
                    // Subtract the (static, scroll-away) header + margins via the theme
                    // spacing scale, and use dvh so it adapts to mobile browser chrome,
                    // so the bottom bar stays inside the viewport even at the page top.
                    height: `calc(100dvh - ${theme.spacing(12)})`,
                    maxHeight: `calc(100dvh - ${theme.spacing(12)})`,
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 1,
                    overflow: "hidden",
                    boxShadow: 2,
                  })}
                >
                  <Box sx={{ position: "relative", flex: 1, overflow: "hidden" }}>
                    <FiledInsertQR
                      ref={insertQRRef}
                      fileUrl={fileUrl}
                      labelQRRef={labelQRRef}
                      filed={{
                        referenceId,
                        referenceCode,
                        folios,
                        createdAt,
                        type: filedType,
                      }}
                      handleFileUpload={handleFileUpload}
                      setLoading={setLoading}
                      onFileLoaded={async () => {
                        setTagEditing(false);
                        setTagInserted(true);
                        // Persist the flag so it survives reloads
                        if (referenceCode) {
                          await saveFiled({
                            filedId: referenceCode,
                            tagInserted: true,
                          });
                        }
                      }}
                    />
                  </Box>
                  {status === "OPEN" && (
                    <Box
                      sx={{
                        flexShrink: 0,
                        px: 2,
                        py: 1.5,
                        display: "flex",
                        gap: 1.5,
                        justifyContent: "center",
                        backgroundColor: "background.paper",
                        borderTop: 1,
                        borderColor: "divider",
                      }}
                    >
                      <Button
                        variant="outlined"
                        size="medium"
                        onClick={() => setTagEditing(false)}
                        sx={{ fontWeight: 600, px: 2.5 }}
                      >
                        Cancelar etiqueta
                      </Button>
                      <Button
                        variant="contained"
                        color="warning"
                        size="large"
                        startIcon={<LocalOfferIcon />}
                        disabled={loading}
                        onClick={() => insertQRRef.current?.applyTag()}
                        sx={{
                          fontWeight: 700,
                          px: 4,
                          animation: loading ? "none" : "applyPulse 1.5s ease-in-out infinite",
                          "@keyframes applyPulse": {
                            "0%": { boxShadow: "0 0 0 0 rgba(237,108,2,0.55)" },
                            "70%": { boxShadow: "0 0 0 14px rgba(237,108,2,0)" },
                            "100%": { boxShadow: "0 0 0 0 rgba(237,108,2,0)" },
                          },
                          "&:hover": { transform: "scale(1.03)" },
                        }}
                      >
                        {loading ? <CircularProgress size={24} /> : <span>Aplicar etiqueta</span>}
                      </Button>
                    </Box>
                  )}
                </Box>
              ) : (
                <>
                  <iframe
                    title="File PDF"
                    src={`${fileUrl}?v=${version}`}
                    width="100%"
                    height="100%"
                    seamless="seamless"
                    scrolling="no"
                    frameBorder="1"
                    allowFullScreen={true}
                  />
                  {status === "OPEN" && (
                    <Box
                      sx={{
                        position: "sticky",
                        bottom: 0,
                        zIndex: 2,
                        mt: 1.5,
                        px: 2,
                        py: 1.5,
                        display: "flex",
                        gap: 1.5,
                        justifyContent: "center",
                        backgroundColor: "background.paper",
                        borderTop: 1,
                        borderColor: "divider",
                      }}
                    >
                      <Button
                        variant="outlined"
                        size="medium"
                        startIcon={<AutorenewIcon />}
                        onClick={handleDeleteUpload}
                        sx={{ fontWeight: 600, px: 2.5 }}
                      >
                        Actualizar archivo
                      </Button>
                      <Button
                        variant="contained"
                        size="medium"
                        startIcon={<LocalOfferIcon />}
                        onClick={() => setTagEditing(true)}
                        sx={{
                          fontWeight: 700,
                          px: 3,
                          boxShadow: 4,
                          "&:hover": { boxShadow: 6 },
                        }}
                      >
                        Agregar etiqueta
                      </Button>
                    </Box>
                  )}
                </>
              )
            ) : pendingPreviewUrl ? (
              <>
                <iframe
                  title="Documento cargado"
                  src={pendingPreviewUrl}
                  width="100%"
                  height="100%"
                  seamless="seamless"
                  scrolling="no"
                  frameBorder="1"
                  allowFullScreen={true}
                />
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Documento cargado · {folios} folio(s) detectado(s). Genera la etiqueta
                    para continuar.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1, ml: 2 }}
                  disabled={loading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Cambiar archivo
                </Button>
              </>
            ) : (
              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  backgroundColor: "lightgray",
                  border: "2px dashed gray",
                  flexDirection: "column",
                  height: "100%",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <Button
                  variant="contained"
                  disabled={loading || !(referenceCode || flowMode === "doc-first")}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    "Cargar archivo"
                  )}
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      </Container>
      <MySnackbar
        info={infoSnack}
        handleClose={() => setInfoSnack({ open: false })}
      />
      <MyBackdrop open={openBackdrop} />
      <CancelFiledDialog
        open={openCancelDialog}
        filedId={referenceCode}
        finalized={false}
        onClose={() => setOpenCancelDialog(false)}
        onCancelled={handleFiledCancelled}
      />
      <ForwardResponseDialog
        open={openForwardDialog}
        onClose={() => setOpenForwardDialog(false)}
        onForward={async (newRespondedor) => {
          if (referenceCode && rootRadicadoId && newRespondedor) {
            // Use userUID if available, otherwise userId
            const newRespondedorId = newRespondedor.userUID || newRespondedor.userId;
            const newRespondedorName = `${newRespondedor.name || ''} ${newRespondedor.lastName || ''}`.trim();
            
            setRespondedorUserId(newRespondedorId);
            setRespondedorNombre(newRespondedorName);
            
            // Update filed with new responder and create timeline event
            const filed = {
              filedId: referenceCode,
              response: {
                respondedorUserId: newRespondedorId,
                respondedorNombre: newRespondedorName,
                ...(parentRadicadoId ? { parentRadicadoId } : {}),
                ...(rootRadicadoId ? { rootRadicadoId } : {})
              }
            };
            // Create timeline event for forwarding
            const now = new Date();
            const timelineEvent = {
              eventType: "FORWARDED",
              at: now,
              byUserId: user.userUID || user.userId,
              byName: `${user.name || ''} ${user.lastName || ''}`.trim(),
              toUserId: newRespondedorId,
              toName: newRespondedorName,
              eventId: `FORWARDED:${referenceCode}:${newRespondedorId}:${now.getTime()}`
            };
            filed.timelineEvent = timelineEvent;
            filed.timelineRootRadicadoId = rootRadicadoId;
            if ((await saveFiled(filed)) === false) return;
            setInfoSnack({ open: true, message: "Respondedor actualizado con éxito" });
            setOpenForwardDialog(false);
          }
        }}
      />
      <TagMissingDialog
        open={openTagWarning}
        onClose={() => setOpenTagWarning(false)}
        onAddTag={() => {
          setOpenTagWarning(false);
          setTagEditing(true);
        }}
        onConfirmPhysical={() => {
          setOpenTagWarning(false);
          setOpenFiledPreview(true);
        }}
      />
      <FiledPreviewDialog
        open={openFiledPreview}
        item={summaryItem}
        loading={finalizing}
        onClose={() => setOpenFiledPreview(false)}
        onConfirm={handleConfirmFinalize}
      />
      <FiledDoneDialog
        open={openDoneDialog}
        item={{ ...summaryItem, status: "DONE" }}
        onClose={() => {
          setOpenDoneDialog(false);
          navigateBypass("/filedlist");
        }}
      />
      <CloseWarningDialog
        open={blocker.state === "blocked"}
        loading={leaving}
        onStay={() => blocker.reset && blocker.reset()}
        onLeave={async () => {
          setLeaving(true);
          try {
            await autoSave();
          } finally {
            setLeaving(false);
            blocker.proceed && blocker.proceed();
          }
        }}
      />
    </>
  );
}
